<template>
  <div class="dashboard-incident-timeline">
    <div class="it-header">
      <h3 class="it-title">{{ $t('org-dashboard.incident-timeline-title') }}</h3>
      <span v-if="summary" class="it-summary">
        {{ summary.totalIncidents }} {{ $t('org-dashboard.incidents-label').toLowerCase() }}
        <template v-if="summary.pending > 0">
          · <span class="it-pending">{{ summary.pending }} {{ $t('org-dashboard.incident-pending') }}</span>
        </template>
      </span>
    </div>

    <div v-if="loading" class="it-loading">
      <v-progress-linear indeterminate color="#FF5722" height="2" />
    </div>

    <template v-else-if="items.length">
      <table class="it-table">
        <thead>
          <tr>
            <th>{{ $t('org-dashboard.charger') }}</th>
            <th>{{ $t('org-dashboard.location') }}</th>
            <th>{{ $t('org-dashboard.incident-severity') }}</th>
            <th>{{ $t('org-dashboard.incident-started') }}</th>
            <th>{{ $t('org-dashboard.incident-duration') }}</th>
            <th>{{ $t('org-dashboard.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in items"
            :key="item.id"
            class="it-row"
            @click="$emit('charger-click', item.chargingPointId)"
          >
            <td class="it-identifier">{{ item.identifier }}</td>
            <td class="it-location">{{ item.locationName || '-' }}</td>
            <td>
              <span class="it-severity" :class="'it-severity--' + item.severity">
                {{ item.severity }}
              </span>
            </td>
            <td class="it-date">{{ formatDate(item.startedAt) }}</td>
            <td class="it-duration">{{ item.durationHours ? formatDuration(item.durationHours) : '-' }}</td>
            <td>
              <span class="it-status" :class="item.resolvedAt ? 'it-status--resolved' : 'it-status--open'">
                {{ item.resolvedAt ? $t('org-dashboard.incident-resolved') : $t('org-dashboard.incident-open') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="totalItems > items.length" class="it-footer">
        <button class="it-view-all" @click="$emit('view-all')">
          {{ $t('org-dashboard.view-all') }} ({{ totalItems }})
          <v-icon x-small class="ml-1">mdi-arrow-right</v-icon>
        </button>
      </div>
    </template>

    <div v-else class="it-empty">
      <v-icon color="#22c55e" size="32">mdi-check-circle-outline</v-icon>
      <span>{{ $t('org-dashboard.incident-none') }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardIncidentTimeline',

  props: {
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    summary: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDuration(hours) {
      const totalMinutes = Math.round(hours * 60)
      const d = Math.floor(totalMinutes / 1440)
      const h = Math.floor((totalMinutes % 1440) / 60)
      const m = totalMinutes % 60
      if (d > 0) return (h > 0 ? `${d}j ${h}h` : `${d}j`) + (m > 0 ? ` ${m}min` : '')
      if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`
      return `${m}min`
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-incident-timeline {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.it-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.it-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.it-summary {
  font-size: 11px;
  color: #6b7280;
}

.it-pending {
  color: #ef4444;
  font-weight: 600;
}

.it-loading {
  margin: 20px 0;
}

.it-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 8px 10px;
    border-bottom: 1px solid #f3f4f6;
  }

  td {
    padding: 10px;
    border-bottom: 1px solid #f9fafb;
    color: #374151;
  }
}

.it-row {
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }
}

.it-identifier {
  font-weight: 600;
  color: #1f2937;
}

.it-location {
  color: #6b7280;
}

.it-date {
  white-space: nowrap;
  color: #6b7280;
  font-size: 11px;
}

.it-duration {
  font-weight: 600;
  color: #1f2937;
}

.it-severity {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;

  &--incident {
    background: #fff7ed;
    color: #c2410c;
  }

  &--critical {
    background: #fef2f2;
    color: #dc2626;
  }
}

.it-status {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;

  &--resolved {
    background: #ecfdf5;
    color: #059669;
  }

  &--open {
    background: #fef2f2;
    color: #dc2626;
  }
}

.it-footer {
  text-align: center;
  padding-top: 12px;
}

.it-view-all {
  font-size: 12px;
  font-weight: 600;
  color: #FF5722;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.it-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;

  span {
    font-size: 12px;
    color: #9ca3af;
  }
}
</style>
